import { Mutation, Action, VuexModule, getModule, Module } from 'vuex-module-decorators';
import { RootState, store } from '../..';
import { evalSettingService } from '@/service/api/eval-setting.service';
import { GradeSchoolClass, SchoolClass } from '@/models/school-class/school-class';
import { ExamineeStudentProgress } from '@/models/evalSetting/evaluator-student-progress';
import FileSaver from 'file-saver';
import { EvalSetting, GrowCourseScoreUpdateStatus } from '@/models/evalSetting/eval-setting';
import {} from '@/models/evalSetting/report-status';
import { DownloadRandomEvalRequests } from '@/models/evalSetting/download-random-eval-request';
import { Tag } from '@/models/tag';
import { EvaluatorCandidateStudent, EvaluatorCandidateStudentRequest } from '@/models/evalSetting/evaluator-candidate-student';
import { GEnum } from '@/constants/enum.defination';

// コース詳細画面用のstore
export interface IEvalSettingDetailState {
  examineeStudentProgresses: ExamineeStudentProgress[]; // あるコースを受検している生徒の進捗状況
  selectedSchoolClassID: number;
  selectedGrade: number;
  examineeStudentProgressesByGrade: Map<Number, ExamineeStudentProgress[]>; // 学年別生徒進捗
  examineeStudentProgressesBySchoolClassID: Map<Number, ExamineeStudentProgress[]>; // クラス別生徒進捗
  examineeSchoolClassesByGrade: GradeSchoolClass[]; // 学年ごとのクラス
  countByTagID: Map<number, number>;
  examineeTags: Tag[];
  evaluatorCandidateStudents: EvaluatorCandidateStudent[]; // 評価者追加の候補となる生徒
  isAllStudentCompleated: boolean; // 全ての生徒が受検完了しているかどうか
  isScoreUpdateRequired: boolean; // 計算フラグ
  isFixed: boolean; // 確定値フラグ
  isForceReport: boolean;
  fixedLavedDispFlag: boolean; // 確定値ラベルを表示するかフラグ
  isDownloadProgressRunning: boolean;
}
@Module({ dynamic: true, store, name: 'evalSettingDetailModule', namespaced: true })
class EvalSettingDetailModule extends VuexModule implements IEvalSettingDetailState {
  examineeStudentProgresses: ExamineeStudentProgress[] = [];
  selectedSchoolClassID: number = 0;
  selectedGrade: number = 0;
  examineeStudentProgressesByGrade: Map<number, ExamineeStudentProgress[]> = new Map<number, ExamineeStudentProgress[]>();
  examineeStudentProgressesBySchoolClassID: Map<number, ExamineeStudentProgress[]> = new Map<number, ExamineeStudentProgress[]>();
  examineeSchoolClassesByGrade: GradeSchoolClass[] = [];
  countByTagID: Map<number, number> = new Map<number, number>();
  countByPriorityTag: Map<number, number> = new Map<number, number>();
  examineeTags: Tag[] = [];
  evaluatorCandidateStudents: EvaluatorCandidateStudent[] = [];
  isAllStudentCompleated: boolean = false;
  isScoreUpdateRequired: boolean = true;
  isFixed: boolean = false;
  isForceReport: boolean = false;
  fixedLavedDispFlag: boolean = false;
  growCourseScoreUpdateAt: Date = new Date();
  isDownloadProgressRunning: boolean = false;

  @Mutation
  setCountByTagID(value: ExamineeStudentProgress[]) {
    this.countByTagID = value.reduce((result: Map<number, number>, studentProgress: ExamineeStudentProgress) => {
      studentProgress.tags.forEach(t => {
        if (!result.has(t.tagID)) {
          result.set(t.tagID, 0);
        }
        result.set(t.tagID, result.get(t.tagID) + 1);
      });
      return result;
    }, new Map<number, number>([]));
  }

  @Mutation
  setCountByPriorityTag(value: ExamineeStudentProgress[]) {
    this.countByPriorityTag = value.reduce((result: Map<number, number>, studentProgress: ExamineeStudentProgress) => {
      const priorityTagID = studentProgress.priorityTag.tagID;
      if (priorityTagID > 0) {
        if (isNaN(result.get(priorityTagID))) {
          result.set(priorityTagID, 1);
        } else {
          result.set(priorityTagID, result.get(priorityTagID) + 1);
        }
      }
      return result;
    }, new Map<number, number>([]));
  }

  @Mutation
  setExamineeTags(value: ExamineeStudentProgress[]) {
    let tagSet: Set<number> = new Set<number>();
    this.examineeTags = value.reduce((tags: Tag[], studentProgress: ExamineeStudentProgress) => {
      studentProgress.tags.forEach(t => {
        if (!tagSet.has(t.tagID)) {
          tags.push(t);
          tagSet.add(t.tagID);
        }
      });
      return tags;
    }, []);
  }

  @Mutation
  setExamineeStudentProgressByGrade(value: ExamineeStudentProgress[]) {
    this.examineeStudentProgressesBySchoolClassID = value.reduce((result: Map<number, ExamineeStudentProgress[]>, examineeStudentProgress: ExamineeStudentProgress) => {
      result.has(examineeStudentProgress.schoolClass.schoolClassID)
        ? result.get(examineeStudentProgress.schoolClass.schoolClassID).push(examineeStudentProgress)
        : result.set(examineeStudentProgress.schoolClass.schoolClassID, [examineeStudentProgress]);
      return result;
    }, new Map<number, ExamineeStudentProgress[]>());
  }

  @Mutation
  setExamineeStudentProgressBySchoolClassID(value: ExamineeStudentProgress[]) {
    this.examineeStudentProgressesByGrade = value.reduce((result: Map<number, ExamineeStudentProgress[]>, examineeStudentProgress: ExamineeStudentProgress) => {
      result.has(examineeStudentProgress.schoolClass.grade)
        ? result.get(examineeStudentProgress.schoolClass.grade).push(examineeStudentProgress)
        : result.set(examineeStudentProgress.schoolClass.grade, [examineeStudentProgress]);
      return result;
    }, new Map<number, ExamineeStudentProgress[]>());
  }

  @Mutation
  setExamineeSchoolClassesByGrade(value: ExamineeStudentProgress[]) {
    let uniqueGradetSet: Set<Number> = new Set<Number>();
    let uniqueSchoolClassSet: Set<Number> = new Set<Number>();

    this.examineeSchoolClassesByGrade = value
      .reduce((result: GradeSchoolClass[], examineeStudentProgress: ExamineeStudentProgress) => {
        // 生徒情報から学年-クラスの包含関係を抽出
        if (uniqueGradetSet.has(examineeStudentProgress.schoolClass.grade)) {
          if (uniqueSchoolClassSet.has(examineeStudentProgress.schoolClass.schoolClassID)) {
            return result;
          }
          uniqueSchoolClassSet.add(examineeStudentProgress.schoolClass.schoolClassID);
          const index = result.findIndex((gradeSchoolClass: GradeSchoolClass) => gradeSchoolClass.grade === examineeStudentProgress.schoolClass.grade);
          result[index].schoolClasses.push(examineeStudentProgress.schoolClass);
          return result;
        }
        uniqueGradetSet.add(examineeStudentProgress.schoolClass.grade);
        uniqueSchoolClassSet.add(examineeStudentProgress.schoolClass.schoolClassID);
        result.push(
          new GradeSchoolClass().deserialize({
            grade: examineeStudentProgress.schoolClass.grade,
            schoolClasses: [examineeStudentProgress.schoolClass],
          })
        );
        return result;
      }, [])
      .map((gradeSchoolClass: GradeSchoolClass) => {
        // クラス順をソート
        gradeSchoolClass.schoolClasses.sort((a: SchoolClass, b: SchoolClass) => {
          // 文字列の短い順
          if (a.schoolClassName.length < b.schoolClassName.length) return -1;
          else if (a.schoolClassName.length > b.schoolClassName.length) return 1;
          if (a.schoolClassName > b.schoolClassName) return 1; // 昇順
          return -1;
        });
        return gradeSchoolClass;
      });
  }

  @Mutation
  setIsAllStudentCompleated(value: ExamineeStudentProgress[]) {
    // 生徒全員が受検完了であることをチェック
    this.isAllStudentCompleated = value.every(progress => {
      return progress.status === GEnum.StatusProgress.Completed;
    });
  }

  @Mutation
  setIsFixed(value: boolean) {
    this.isFixed = value;
  }

  @Mutation
  setSelectedSchoolClassID(value: number) {
    this.selectedSchoolClassID = value;
  }

  @Mutation
  setSelectedGrade(value: number) {
    this.selectedGrade = value;
  }

  @Mutation
  setExamineeStudentProgresses(value: ExamineeStudentProgress[]) {
    this.examineeStudentProgresses = value;
  }

  @Mutation
  clean() {
    this.selectedGrade = 0;
    this.selectedSchoolClassID = 0;
    this.examineeStudentProgresses = [];
    this.isAllStudentCompleated = false;
    this.isScoreUpdateRequired = true;
    this.isFixed = false;
    this.isForceReport = false;
    this.fixedLavedDispFlag = false;
    this.isDownloadProgressRunning = false;
  }

  @Mutation
  setEvaluatorCandidateStudents(value: EvaluatorCandidateStudent[]) {
    this.evaluatorCandidateStudents = value;
  }

  @Mutation
  setIsScoreUpdateRequired(value: boolean) {
    this.isScoreUpdateRequired = value;
  }

  @Mutation
  setFixedLavedDispFlag(value: boolean) {
    this.fixedLavedDispFlag = value;
  }

  @Mutation
  setIsForceReport(value: boolean) {
    this.isForceReport = value;
  }

  @Mutation
  setGrowCourseScoreUpdateAt(value: any) {
    value ? (this.growCourseScoreUpdateAt = new Date(value)) : (this.growCourseScoreUpdateAt = null);
  }

  @Mutation
  setIsDownloadProgressRunning(value: boolean) {
    this.isDownloadProgressRunning = value;
  }

  /**
   * 計算フラグ取得
   * @param giftCourseID
   */
  @Action({ rawError: true })
  async setGrowCourseScoreUpdateStatus(giftCourseID: number) {
    // 計算フラグ取得
    const scoreUpdateStatus: GrowCourseScoreUpdateStatus = await evalSettingService.getUpdateStatusByGiftCourseID(giftCourseID);
    this.setIsScoreUpdateRequired(scoreUpdateStatus.state ? scoreUpdateStatus.updateRequired : false);
    this.setFixedLavedDispFlag(scoreUpdateStatus.state);
    this.setIsFixed(scoreUpdateStatus.isFixed);
    this.setIsForceReport(scoreUpdateStatus.isForceReport);
    this.setGrowCourseScoreUpdateAt(scoreUpdateStatus.state ? scoreUpdateStatus.updatedAt : null);
  }

  /**
   * 計算フラグ及び確定値フラグ更新、レコードがない場合は登録
   * @param giftCourseID
   * @param isFixed
   */
  @Action({ rawError: true })
  async updateScoreUpdateRequired(giftCourseID: number) {
    const scoreUpdateStatus: GrowCourseScoreUpdateStatus = await evalSettingService.updateOrInsertScoreUpdateStatus(giftCourseID, { isFixed: this.isFixed, isForceReport: this.isForceReport });
    this.setIsScoreUpdateRequired(scoreUpdateStatus.state ? scoreUpdateStatus.updateRequired : false);
    this.setFixedLavedDispFlag(scoreUpdateStatus.state);
    this.setIsFixed(scoreUpdateStatus.isFixed);
    this.setIsForceReport(scoreUpdateStatus.isForceReport);
    this.setGrowCourseScoreUpdateAt(scoreUpdateStatus.state ? scoreUpdateStatus.updatedAt : null);
  }

  @Action({ rawError: true })
  async fetchExamineeStudentProgresses(evalSettingID: number) {
    const examineeStudentProgresses: ExamineeStudentProgress[] = await evalSettingService.fetchExamineeStudentProgresses(evalSettingID);
    this.setExamineeStudentProgresses(examineeStudentProgresses);
    this.setExamineeStudentProgressByGrade(examineeStudentProgresses);
    this.setExamineeStudentProgressBySchoolClassID(examineeStudentProgresses);
    this.setExamineeSchoolClassesByGrade(examineeStudentProgresses);
    this.setCountByTagID(examineeStudentProgresses);
    this.setCountByPriorityTag(examineeStudentProgresses);
    this.setExamineeTags(examineeStudentProgresses);
    this.setIsAllStudentCompleated(examineeStudentProgresses);

    if (examineeStudentProgresses.length > 0) {
      // 初期表示学年をセット
      this.setSelectedGrade(this.examineeSchoolClassesByGrade[0].grade);
      // 初期表示クラスをセット
      this.setSelectedSchoolClassID(this.examineeSchoolClassesByGrade[0].schoolClasses[0].schoolClassID);
    }
  }

  /**
   * 評価者追加の候補となる生徒取得
   * @param request コースID,生徒ID
   */
  @Action({ rawError: true })
  async fetchEvaluatorCandidateStudents(request: EvaluatorCandidateStudentRequest): Promise<void> {
    const evaluatorCandidateStudents: EvaluatorCandidateStudent[] = await evalSettingService.fetchEvaluatorCandidateStudents(request);
    this.setEvaluatorCandidateStudents(evaluatorCandidateStudents);
  }

  /**
   * 受検進捗ダウンロード
   * @param data 受検設定
   */
  @Action({ rawError: true })
  async downloadProgress(data: EvalSetting) {
    const file = await evalSettingService.downloadProgress(data.evalSettingID);
    FileSaver.saveAs(file, `${data.title}.xlsx`);
  }

  /**
   * 自動生成評価リクエストダウンロード
   * @param request コースID,対象ドメインタイプ
   */
  @Action({ rawError: true })
  async downloadRandomEvalRequests(request: DownloadRandomEvalRequests) {
    const file = await evalSettingService.downloadRandomEvalRequests(request.evalSettingID, request.targetDomainType, request.isPriorityTag, request.tagID);
    FileSaver.saveAs(file, `自動評価組み合わせ(${request.title})_${request.targetDomainTypeName}.xlsx`);
  }

  /**
   * 登録済み評価リクエストダウンロード
   * @param request コースID,対象ドメインタイプ
   */
  @Action({ rawError: true })
  async downloadRegisteredEvalRequests(request: DownloadRandomEvalRequests) {
    const file = await evalSettingService.downloadRegisteredEvalRequests(request.evalSettingID);
    FileSaver.saveAs(file, `登録済み評価リクエスト一覧(${request.title}).xlsx`);
  }
}

export const evalSettingDetailModule = getModule(EvalSettingDetailModule);
